import { useCallback, useMemo } from "react";
import useClerkMetadata from "./useClerkMetadata";
import { OnboardingData, Role } from "features/auth/types/onboarding";
import { isStudentRole as isStudentRoleUtil } from "features/auth/utils/roleUtils";

export interface OnboardingDataResponse {
  // Core onboarding data
  role?: Role;
  otherRoleText?: string;
  libraryId?: string;
  libraryName?: string;
  verifiedStudentEmail?: string;

  // Status flags
  hasCompletedOnboarding: boolean; // true only if fully completed (not skipped)
  hasSkippedOnboarding: boolean;
  completedViaStudentDiscount: boolean;

  // Helper methods
  isStudentRole: () => boolean;

  // Update methods
  updateOnboardingData: (
    data: Partial<OnboardingData>,
    source?: "student-discount"
  ) => Promise<void>;
}

/**
 * Hook that provides clean access to onboarding data with proper handling
 * of skipped onboarding and tracking of completion source.
 *
 * Key features:
 * - Distinguishes between skipped and completed onboarding
 * - Tracks if onboarding was completed via student discount flow
 * - Only returns role data if onboarding was fully completed
 */
export function useOnboardingData(): OnboardingDataResponse {
  const { userMetadata, updateUserMetadata } = useClerkMetadata();

  const onboardingData = userMetadata?.onboarding_data;

  // Determine onboarding status
  const hasSkippedOnboarding = useMemo(() => {
    return onboardingData?.skipped === true;
  }, [onboardingData?.skipped]);

  const hasCompletedOnboarding = useMemo(() => {
    return !hasSkippedOnboarding && !!onboardingData?.responses?.role;
  }, [hasSkippedOnboarding, onboardingData?.responses?.role]);

  const completedViaStudentDiscount = useMemo(() => {
    return onboardingData?.completion_source === "student-discount";
  }, [onboardingData?.completion_source]);

  // Helper to check if user has a student role
  const isStudentRole = useCallback(() => {
    if (!hasCompletedOnboarding) return false;
    const role = onboardingData?.responses?.role;
    return isStudentRoleUtil(role);
  }, [hasCompletedOnboarding, onboardingData?.responses?.role]);

  // Update onboarding data with optional source tracking
  const updateOnboardingData = useCallback(
    async (data: Partial<OnboardingData>, source?: "student-discount") => {
      if (!userMetadata) return;

      const updatedData = {
        onboarding_data: {
          skipped: false,
          responses: {
            ...onboardingData?.responses,
            ...data,
          },
          ...(source && { completion_source: source }),
        },
      };

      await updateUserMetadata(updatedData);
    },
    [userMetadata, onboardingData?.responses, updateUserMetadata]
  );

  return {
    // Data - only return if onboarding was completed (not skipped)
    role: hasCompletedOnboarding ? onboardingData?.responses?.role : undefined,
    otherRoleText: hasCompletedOnboarding
      ? onboardingData?.responses?.other_role_text
      : undefined,
    libraryId: hasCompletedOnboarding
      ? onboardingData?.responses?.library_id
      : undefined,
    libraryName: hasCompletedOnboarding
      ? onboardingData?.responses?.library_name
      : undefined,
    verifiedStudentEmail: hasCompletedOnboarding
      ? onboardingData?.responses?.verified_student_email
      : undefined,

    // Status
    hasCompletedOnboarding,
    hasSkippedOnboarding,
    completedViaStudentDiscount,

    // Helpers
    isStudentRole,

    // Update
    updateOnboardingData,
  };
}

export default useOnboardingData;
